/**
 * NotFoundPage — Bayenah V3 catch-all route.
 *
 * Rendered when the user navigates to an unknown URL (Test 17.2).
 * Keeps the Harvey black/white aesthetic and gives the user one
 * obvious return path back into the app instead of a blank screen.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';

export default function NotFoundPage() {
  const { language } = useLanguage();
  const isArabic = language !== 'en';

  return (
    <main
      data-testid="not-found-page"
      dir={isArabic ? 'rtl' : 'ltr'}
      className="min-h-screen flex items-center justify-center bg-white px-6 font-arabic"
    >
      <div className="max-w-md text-center">
        <div
          aria-hidden="true"
          className="mx-auto mb-6 w-14 h-14 rounded-full bg-[#F5F5F3] border border-[#E8E8E8] flex items-center justify-center"
        >
          <Compass className="w-6 h-6 text-[#111111]" strokeWidth={1.7} />
        </div>
        <p
          className="text-[12px] uppercase tracking-[0.18em] text-[#999999] mb-3"
          data-testid="not-found-code"
        >
          {isArabic ? 'الصفحة غير موجودة · 404' : '404 · Page not found'}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#111111] mb-3">
          {isArabic ? 'وصلت إلى مسار غير معروف' : "You've reached an unknown path"}
        </h1>
        <p className="text-[13px] text-[#666666] leading-relaxed mb-7">
          {isArabic
            ? 'الرابط الذي حاولت زيارته لم يَعُد متاحاً، أو ربما كُتب بشكل غير صحيح. لنُعدك إلى موضع آمن.'
            : 'The link you tried is no longer available or may have been mistyped. Let us bring you back to safe ground.'}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-[#111111] text-white text-[13px] px-5 py-2 hover:bg-black/85 transition"
            data-testid="not-found-home-cta"
          >
            {isArabic ? 'العودة إلى الرئيسية' : 'Back to home'}
          </Link>
          <Link
            to="/chat"
            className="inline-flex items-center gap-2 rounded-full border border-[#E8E8E8] text-[#111111] text-[13px] px-5 py-2 hover:border-[#999999] transition"
            data-testid="not-found-chat-cta"
          >
            {isArabic ? 'فتح المستشار' : 'Open assistant'}
          </Link>
        </div>
      </div>
    </main>
  );
}
